import { Fragment, useEffect, useRef, useState } from "react";

import { Pager, useHeight } from "../ui/paging";
import type { Follow } from "../ui/paging";
import styles from "./inventory.module.css";

/** Every row in both lists is this tall, which is what lets a page be counted. */
export const SHELF_ROW = 42;

export interface ShelfRow {
  key: string;
  node: React.ReactNode;
}

/**
 * One list card, for the stock and for the procedure trays (IV3) — and for the
 * procedure list on Treatment plans (TR8), which is why its rows are 42px too.
 *
 * **The search sits in the card's own header and the pager in its foot**, so the
 * card starts and ends on the same lines as the panel beside it — the client's
 * ask on the prototype: *"embed the search bar in the container and have it the
 * same size of the side container"*.
 *
 * **Pages, never a scroll bar** (PF4): a page is however many whole rows the
 * card's height holds, recounted when the window changes. Where the page cannot
 * be one screen tall — a phone, a narrow window — the area grows with its rows
 * and everything lands on one page, which the page itself then scrolls.
 */
export function ShelfList({
  head,
  columns,
  rows,
  empty,
  noun,
  follow,
  resetOn,
  testId,
}: {
  head: React.ReactNode;
  /** The column labels, when the rows have more than one thing in them. */
  columns?: React.ReactNode;
  rows: readonly ShelfRow[];
  empty: string;
  /** For the pager: "12–24 of 40 items". */
  noun: string;
  /** The row somebody just added or picked; the list turns to its page. */
  follow?: Follow | null;
  /** Back to the first page when this changes — a new search, another location. */
  resetOn?: unknown;
  testId?: string;
}): React.JSX.Element {
  const [areaRef, areaHeight] = useHeight<HTMLDivElement>();
  const [page, setPage] = useState(0);

  // Before the area has a height, and wherever it grows with its rows, every
  // row is on the one page.
  const pageSize =
    areaHeight <= 0 ? Math.max(1, rows.length) : Math.max(1, Math.floor(areaHeight / SHELF_ROW));
  const count = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, count - 1);

  useEffect(() => {
    setPage(0);
  }, [resetOn]);

  /**
   * Follow the row somebody is working on onto whichever page holds it — a new
   * item lands wherever the sort puts it. Turning a page by hand lets go, so the
   * list does not snap back on the next reload.
   */
  const released = useRef<Follow | null>(null);
  useEffect(() => {
    if (follow === undefined || follow === null || released.current === follow) return;
    const index = rows.findIndex((row) => row.key === follow.key);
    if (index < 0) return;
    setPage(Math.floor(index / pageSize));
  }, [follow, rows, pageSize]);

  const turn = (next: number): void => {
    released.current = follow ?? null;
    setPage(next);
  };

  const start = current * pageSize;
  const shown = rows.slice(start, start + pageSize);

  return (
    <section className={styles.listCard} data-testid={testId}>
      <div className={styles.cardHead}>{head}</div>
      {columns}
      <div className={styles.listArea} ref={areaRef}>
        {rows.length === 0 ? (
          <p className={styles.blank}>{empty}</p>
        ) : (
          shown.map((row) => <Fragment key={row.key}>{row.node}</Fragment>)
        )}
      </div>
      <div className={styles.cardFoot}>
        <Pager
          page={current}
          count={count}
          onPage={turn}
          from={start + 1}
          to={start + shown.length}
          total={rows.length}
          noun={noun}
          testId={testId === undefined ? undefined : `${testId}-pager`}
        />
      </div>
    </section>
  );
}
